import { CHECK_OUT_STAMP_ROOT, isCheckOutStamp } from "./checkOutStamp";

// A console harness for the questions that can only be answered inside a real Office
// host. Most of an add-in cannot be unit tested, and the alternative to this is asking
// someone to paste a different one-line snippet per question -- which is how a single
// PowerPoint bug turned into four rounds of relaying console output, each answering one
// thing and raising two more.
//
// So: one function, everything it can measure in a single pass, printed as one block to
// copy back. Read-only unless explicitly asked otherwise.
//
//   jyloDiag()                 what the host reports about this document
//   jyloDiag({ write: true })  ... and then add a custom XML part and re-read
//
// The write probe is opt-in because it MUTATES the open document: it adds a part that
// nothing removes. Harmless on a scratch deck, not something to fire on a user's work.

interface DiagOptions {
  /** Add a custom XML part, then re-read. Mutates the document. */
  write?: boolean;
  /** Namespace for the written probe. Empty string writes a part with none. */
  namespace?: string;
}

interface PartReport {
  id?: string;
  namespaceUri?: string;
  isStamp: boolean;
  xml: string;
}

/** Whichever of Excel/PowerPoint is running; both expose the same shape here. */
interface AnyXmlPart {
  id: string;
  namespaceUri: string;
  getXml(): { value: string };
}

interface AnyXmlCollection {
  readonly items: AnyXmlPart[];
  add(xml: string): unknown;
  load(propertyNames: string): unknown;
  getCount(): { value: number };
}

interface AnyContext {
  sync(): Promise<void>;
}

/** Reads a property the host may not have loaded, without taking the whole run down. */
function tryRead<T>(read: () => T): T | undefined {
  try {
    return read();
  } catch {
    return undefined;
  }
}

function requirementSets(): Record<string, boolean> {
  const sets: Record<string, boolean> = {};
  const check = (name: string, version: string) => {
    const supported = tryRead(() => Office.context.requirements.isSetSupported(name, version));
    if (supported !== undefined) sets[`${name} ${version}`] = supported;
  };
  check("SharedRuntime", "1.1");
  for (const v of ["1.1", "1.3", "1.5", "1.7", "1.8", "1.9", "1.10"]) {
    check("PowerPointApi", v);
    check("ExcelApi", v);
  }
  check("WordApi", "1.4");
  return sets;
}

function environment(): Record<string, unknown> {
  return {
    // Which file this report describes. Without it a report is unattributable, and two
    // decks whose contents differ can produce output you cannot tell apart -- which is
    // exactly what happened once, costing a round trip to notice.
    document: tryRead(() => Office.context.document?.url),
    host: tryRead(() => Office.context.host as unknown as string),
    platform: tryRead(() => Office.context.platform as unknown as string),
    officeVersion: tryRead(() => Office.context.diagnostics?.version),
    officeHost: tryRead(() => Office.context.diagnostics?.host as unknown as string),
    url: window.location.href,
  };
}

async function inspect(
  parts: AnyXmlCollection,
  context: AnyContext
): Promise<{ getCount: number; items: number; parts: PartReport[] }> {
  const count = parts.getCount();
  parts.load("items");
  await context.sync();

  // Queue every getXml() before syncing: one round trip rather than one per part.
  const xml = parts.items.map((part) => part.getXml());
  await context.sync();

  return {
    // eslint-disable-next-line office-addins/load-object-before-read -- ClientResult, not a loadable proxy
    getCount: count.value,
    items: parts.items.length,
    parts: parts.items.map((part, index) => ({
      id: tryRead(() => part.id),
      namespaceUri: tryRead(() => part.namespaceUri),
      isStamp: isCheckOutStamp(xml[index].value),
      xml: xml[index].value.slice(0, 300),
    })),
  };
}

/**
 * Runs the whole battery and prints one block.
 *
 * Resolves to the report as well as logging it, so it can be awaited and inspected
 * rather than only read.
 */
export async function runDiagnostics(options: DiagOptions = {}): Promise<unknown> {
  const report: Record<string, unknown> = {
    environment: environment(),
    requirementSets: requirementSets(),
    stampRoot: CHECK_OUT_STAMP_ROOT,
  };

  const host = report.environment as { host?: string };
  const runner =
    host.host === "Excel"
      ? (cb: (c: AnyContext) => Promise<void>) =>
          Excel.run(cb as unknown as (c: Excel.RequestContext) => Promise<void>)
      : host.host === "PowerPoint"
        ? (cb: (c: AnyContext) => Promise<void>) =>
            PowerPoint.run(cb as unknown as (c: PowerPoint.RequestContext) => Promise<void>)
        : null;

  if (!runner) {
    report.customXml = `no shared custom XML API for host '${host.host}' (Word reads them differently)`;
  } else {
    try {
      await runner(async (context) => {
        const parts = (host.host === "Excel"
          ? (context as unknown as Excel.RequestContext).workbook.customXmlParts
          : (context as unknown as PowerPoint.RequestContext).presentation
              .customXmlParts) as unknown as AnyXmlCollection;

        report.beforeWrite = await inspect(parts, context);

        if (options.write) {
          const ns = options.namespace ?? "https://jylo.ai/diagnostic/1.0";
          const probe = ns
            ? `<jyloDiagnosticProbe xmlns="${ns}">written-by-api</jyloDiagnosticProbe>`
            : `<jyloDiagnosticProbe>written-by-api</jyloDiagnosticProbe>`;
          parts.add(probe);
          await context.sync();
          report.wrote = probe;
          report.afterWrite = await inspect(parts, context);
        }
      });
    } catch (error) {
      report.customXmlError = String(error);
    }
  }

  console.log("=== Jylo diagnostics ===\n" + JSON.stringify(report, null, 2));
  return report;
}

/**
 * Puts the harness on `window` so it can be called from the pane's console.
 *
 * Registered unconditionally rather than behind a build flag: the failures worth
 * diagnosing happen in a real host on someone else's machine, which is exactly where a
 * development-only tool is not present. It reads nothing on its own and mutates nothing
 * unless asked.
 */
export function registerDiagnostics(): void {
  (window as unknown as Record<string, unknown>).jyloDiag = runDiagnostics;
}
